import { Check, Loader2 } from 'lucide-react';
import { cn } from '../../lib/cn';
import { STAGES } from '../../lib/format';
import type { ReviewSummary } from '../../types/api';

/** Step-by-step progress for queued/processing reviews (polled from the server). */
export function ReviewProgress({ review }: { review: ReviewSummary }) {
  const current = Math.max(0, STAGES.findIndex((s) => s.id === review.stage));
  return (
    <div className="card p-5" role="status" aria-live="polite" aria-label="Review progress">
      <div className="flex items-center gap-2 text-sm font-medium">
        <Loader2 className="h-4 w-4 animate-spin text-accent" aria-hidden />
        Review in progress — {STAGES[current]?.label ?? 'Working'}…
      </div>
      <ol className="mt-4 grid gap-2 sm:grid-cols-3 lg:grid-cols-6">
        {STAGES.map((s, i) => {
          const done = i < current;
          const active = i === current;
          return (
            <li
              key={s.id}
              className={cn(
                'flex items-center gap-2 rounded-md border px-2.5 py-2 text-xs',
                done && 'border-ok/30 text-ok',
                active && 'border-accent/40 bg-accent/5 text-ink',
                !done && !active && 'border-line text-faint',
              )}
            >
              {done ? (
                <Check className="h-3.5 w-3.5 shrink-0" aria-hidden />
              ) : active ? (
                <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" aria-hidden />
              ) : (
                <span className="h-3.5 w-3.5 shrink-0 rounded-full border border-line" aria-hidden />
              )}
              <span>{s.label}</span>
              <span className="sr-only">{done ? '(done)' : active ? '(in progress)' : '(pending)'}</span>
            </li>
          );
        })}
      </ol>
      <p className="mt-3 text-xs text-faint">This page updates automatically. Larger PRs can take a minute or two.</p>
    </div>
  );
}
